/**
 * Протокол realtime-мультиплеера редактора (WS `/api/projects/:id/realtime`).
 *
 * Модель — server-authoritative (как у Felt): правки идут REST-ом, сервер после записи
 * ребродкастит data-событие в комнату проекта; presence (курсоры/выделения) — эфемерные.
 * Автор правки узнаёт себя по `clientId` (uuid per-tab, заголовок `x-sloy-client` в REST).
 */

/** Позиция курсора участника на карте. */
export interface RealtimeCursor {
  lng: number;
  lat: number;
}

/** Выделение участника (для будущей подсветки чужого выделения). */
export interface RealtimeSelection {
  layerId: string | null;
  /** Может быть урезан до потолка (docs/editing/plan.md §1.8) — честный размер в `count`. */
  featureIds: string[];
  /** Реальный размер выделения (может быть больше `featureIds.length`, если список урезан). */
  count?: number;
}

/** Вьюпорт участника — для режима «следовать» (клик по аватарке, как в Figma/Miro). */
export interface RealtimeViewport {
  lng: number;
  lat: number;
  zoom: number;
  pitch: number;
  bearing: number;
}

/** Публичное presence-состояние одного соединения (вкладки). */
export interface PeerPresence {
  /** Id соединения (уникален в пределах комнаты; у одного юзера может быть несколько вкладок). */
  connId: string;
  userId: string;
  name: string;
  /** Цвет из RT_PEER_COLORS, детерминированно по userId. */
  color: string;
  avatarUrl?: string | null;
  cursor?: RealtimeCursor | null;
  selection?: RealtimeSelection | null;
  viewport?: RealtimeViewport | null;
}

/* ------------------------------------------------------------------ */
/*  Data-события (после успешной записи в БД)                          */
/* ------------------------------------------------------------------ */

interface RealtimeDataEventBase {
  /** clientId вкладки-автора; вкладка с тем же id событие игнорирует. */
  clientId: string | null;
  /** userId автора (для отладки/будущих бейджей «кто поменял»). */
  actorId: string;
}

/** Минимальный структурный Feature (в shared нет зависимости на @types/geojson). */
export interface RealtimeFeaturePayload {
  type: 'Feature';
  id?: string | number;
  geometry: unknown;
  properties: Record<string, unknown> | null;
}

export interface RealtimeFeatureUpsertEvent extends RealtimeDataEventBase {
  kind: 'feature:created' | 'feature:updated';
  layerId: string;
  /** Полное тело фичи из ответа REST (истина с сервера) — адресный патч кэша без рефетча. */
  feature: RealtimeFeaturePayload;
}

export interface RealtimeFeatureDeletedEvent extends RealtimeDataEventBase {
  kind: 'feature:deleted';
  layerId: string;
  featureId: string;
}

export interface RealtimeFeaturesReorderedEvent extends RealtimeDataEventBase {
  kind: 'features:reordered';
  layerId: string;
}

/** Грубое событие по слоям (create/update/reorder) — клиент инвалидирует список слоёв. */
export interface RealtimeLayersChangedEvent extends RealtimeDataEventBase {
  kind: 'layers:changed';
}

export interface RealtimeLayerRemovedEvent extends RealtimeDataEventBase {
  kind: 'layer:removed';
  layerId: string;
}

export interface RealtimeProjectUpdatedEvent extends RealtimeDataEventBase {
  kind: 'project:updated';
}

/**
 * Данные слоя переписаны пачкой мимо REST (integration-sync воркер: pull-реконсиляция).
 * Клиент перезагружает geojson/страницы свойств слоя целиком — адресные события
 * для батча в сотни фич дороже одного рефетча.
 */
export interface RealtimeLayerDataChangedEvent extends RealtimeDataEventBase {
  kind: 'layer:data-changed';
  layerId: string;
}

/**
 * Колонка страниц режима «Навигация» изменилась: добавили, переставили, удалили,
 * поменяли ширину. Клиент перечитывает список страниц целиком — их десятки, а не тысячи,
 * и адресные события здесь дороже одного рефетча.
 */
export interface RealtimeWayfindingFramesChangedEvent extends RealtimeDataEventBase {
  kind: 'wayfinding:frames-changed';
}

/**
 * Стили подложки проекта изменились: создали, переименовали, поправили палитру, удалили.
 * `project:updated` здесь не хватает — список стилей живёт в отдельном запросе, и без этого
 * события стиль, созданный соседом или агентом, не рисовался до ручного обновления.
 */
export interface RealtimeMapStylesChangedEvent extends RealtimeDataEventBase {
  kind: 'map-styles:changed';
}

/** Презентация или её слайды изменились: клиент перечитывает презентацию целиком. */
export interface RealtimePresentationChangedEvent extends RealtimeDataEventBase {
  kind: 'presentation:changed';
}

/** Подключения и привязки интеграций изменились; слои, если их тронуло, — отдельным событием. */
export interface RealtimeIntegrationsChangedEvent extends RealtimeDataEventBase {
  kind: 'integrations:changed';
}

/** Набор шрифтов проекта изменился (загрузка, удаление). */
export interface RealtimeFontsChangedEvent extends RealtimeDataEventBase {
  kind: 'fonts:changed';
}

export type RealtimeDataEvent =
  | RealtimeFeatureUpsertEvent
  | RealtimeFeatureDeletedEvent
  | RealtimeFeaturesReorderedEvent
  | RealtimeLayersChangedEvent
  | RealtimeLayerRemovedEvent
  | RealtimeProjectUpdatedEvent
  | RealtimeLayerDataChangedEvent
  | RealtimeWayfindingFramesChangedEvent
  | RealtimeMapStylesChangedEvent
  | RealtimePresentationChangedEvent
  | RealtimeIntegrationsChangedEvent
  | RealtimeFontsChangedEvent;

/* ------------------------------------------------------------------ */
/*  Клиент → сервер                                                    */
/* ------------------------------------------------------------------ */

export type RealtimeClientMessage =
  | { type: 'auth'; token: string; clientId: string }
  | { type: 'cursor'; lng: number; lat: number }
  | { type: 'cursor:hide' }
  | { type: 'selection'; layerId: string | null; featureIds: string[]; count?: number }
  | { type: 'viewport'; lng: number; lat: number; zoom: number; pitch: number; bearing: number };

/* ------------------------------------------------------------------ */
/*  Сервер → клиент                                                    */
/* ------------------------------------------------------------------ */

export type RealtimeServerMessage =
  | { type: 'welcome'; connId: string; peers: PeerPresence[] }
  | { type: 'peer:join'; peer: PeerPresence }
  | { type: 'peer:leave'; connId: string }
  | { type: 'peer:cursor'; connId: string; cursor: RealtimeCursor | null }
  | { type: 'peer:selection'; connId: string; selection: RealtimeSelection }
  | { type: 'peer:viewport'; connId: string; viewport: RealtimeViewport }
  | { type: 'data'; event: RealtimeDataEvent };

/* ------------------------------------------------------------------ */
/*  Утилиты                                                            */
/* ------------------------------------------------------------------ */

/** Палитра цветов участников (индиго-семейство UI не задевает; контраст на светлой карте). */
export const RT_PEER_COLORS = [
  '#e11d48', // rose
  '#ea580c', // orange
  '#ca8a04', // yellow-dark
  '#16a34a', // green
  '#0d9488', // teal
  '#0284c7', // sky
  '#7c3aed', // violet
  '#c026d3', // fuchsia
] as const;

/** Детерминированный цвет участника по userId (одинаков на всех клиентах). */
export function pickPeerColor(userId: string): string {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = (hash * 31 + userId.charCodeAt(i)) | 0;
  }
  return RT_PEER_COLORS[Math.abs(hash) % RT_PEER_COLORS.length];
}

/** Заголовок REST-запросов с id вкладки-автора (эхо в data-событиях). */
export const RT_CLIENT_HEADER = 'x-sloy-client';
